import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { api, configureApi, type AuthConfig } from './api';
import { tr } from '../i18n';

/**
 * Autenticación del panel:
 *  - OIDC genérico (Authorization Code + PKCE) contra el proveedor configurado en la API
 *    (Microsoft Entra ID en Voxel; vale cualquier IdP estándar).
 *  - Usuario/contraseña local (desarrollo), si la API lo tiene habilitado.
 * El token se guarda en sessionStorage (se pierde al cerrar la pestaña).
 */
interface Session {
  token: string;
  email: string | null;
  name: string | null;
  expiresAt: number;
  method: 'local' | 'oidc';
}

interface AuthContextValue {
  config: AuthConfig | null;
  session: Session | null;
  loading: boolean;
  loginLocal(email: string, password: string): Promise<void>;
  loginOidc(): Promise<void>;
  completeOidc(): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const KEY = 'voxel-admin-session';

let userManager: UserManager | null = null;
function getUserManager(cfg: NonNullable<AuthConfig['oidc']>) {
  if (!userManager) {
    userManager = new UserManager({
      authority: cfg.issuer,
      client_id: cfg.client_id,
      redirect_uri: `${window.location.origin}/auth/callback`,
      post_logout_redirect_uri: `${window.location.origin}/login`,
      response_type: 'code',
      scope: cfg.scopes,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      automaticSilentRenew: false,
    });
  }
  return userManager;
}

function readSession(): Session | null {
  try {
    const s = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as Session | null;
    return s && s.expiresAt > Date.now() ? s : null;
  } catch {
    return null;
  }
}

function sessionFromOidc(user: User, tokenType: string): Session {
  const token = tokenType === 'access_token' ? user.access_token : (user.id_token ?? user.access_token);
  return {
    token,
    email: (user.profile.email as string) ?? (user.profile.preferred_username as string) ?? null,
    name: (user.profile.name as string) ?? null,
    expiresAt: (user.expires_at ?? Math.floor(Date.now() / 1000) + 3600) * 1000,
    method: 'oidc',
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [session, setSession] = useState<Session | null>(() => readSession());
  const [loading, setLoading] = useState(true);

  const store = useCallback((s: Session | null) => {
    if (s) sessionStorage.setItem(KEY, JSON.stringify(s));
    else sessionStorage.removeItem(KEY);
    setSession(s);
  }, []);

  useEffect(() => {
    configureApi({
      getToken: async () => readSession()?.token ?? null,
      onUnauthorized: () => store(null),
    });
    api
      .get<AuthConfig>('/v1/admin/auth/config', { auth: false })
      .then(setConfig)
      .catch(() => setConfig({ local: false, oidc: null }))
      .finally(() => setLoading(false));
  }, [store]);

  const value = useMemo<AuthContextValue>(
    () => ({
      config,
      session,
      loading,
      async loginLocal(email, password) {
        const r = await api.post<{ token: string; expires_at: string; user: { email: string; name: string | null } }>(
          '/v1/admin/auth/login',
          { email, password },
          { auth: false },
        );
        store({ token: r.token, email: r.user.email, name: r.user.name, expiresAt: Date.parse(r.expires_at), method: 'local' });
      },
      async loginOidc() {
        if (!config?.oidc) throw new Error(tr('login.oidcNotConfigured'));
        await getUserManager(config.oidc).signinRedirect();
      },
      async completeOidc() {
        const cfg = config?.oidc;
        if (!cfg) throw new Error(tr('login.oidcNotConfigured'));
        const user = await getUserManager(cfg).signinRedirectCallback();
        store(sessionFromOidc(user, cfg.token_type));
      },
      async logout() {
        const method = session?.method;
        store(null);
        if (method === 'oidc' && config?.oidc) {
          await getUserManager(config.oidc).removeUser();
        }
      },
    }),
    [config, session, loading, store],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fuera de AuthProvider');
  return ctx;
}
